import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { Input, DropDown, Button, DatePicker } from "../../Common";
import {
  getUsersPartnerList,
  getPartnerPlan,
  createNewCoupon,
  updateCoupon,
} from "../../api";
import styles from "./Coupon.module.css";
import { discountTypes, couponStatusList } from "../../utils/utils";
import { validateCouponForm } from "../../utils/validation";

const CreateCoupon = ({ mode = "create", initialData = null, onSuccess }) => {
  const [partnerList, setPartnerList] = useState([]);
  const [planList, setPlanList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState({ type: "", text: "" });

  const [form, setForm] = useState({
    code: "",
    discountType: "",
    discountValue: "",
    startDt: "",
    expiryDt: "",
    usageLimit: "",
    // usageCount: "",
    partnerCode: "",
    planId: "",
    title: "",
    status: "ACTIVE",
    comment: "",
  });

  useEffect(() => {
    if (mode === "update" && initialData) {
      setForm({ ...initialData });
    }
  }, [mode, initialData]);

  useEffect(() => {
    fetchPartnerList();
  }, []);

  useEffect(() => {
    if (form.partnerCode) fetchPlanList(form.partnerCode);
    else setPlanList([]);
  }, [form.partnerCode]);

  const fetchPartnerList = async () => {
    const resp = await getUsersPartnerList({ appName: "INVESTING" });
     if (resp?.data?.code === 200) {
      setPartnerList(resp.data.data);
  }
  };

  const fetchPlanList = async (partnerCode) => {
    const resp = await getPartnerPlan(partnerCode, {});
    if (resp?.data?.code === 200) {
      setPlanList(resp.data.data);
    }
  };

  const handleChange = (f, v) => {
    setForm((prev) => ({ ...prev, [f]: v }));
    setErrors((prev) => ({ ...prev, [f]: "" }));
  };

  const handleSubmit = async () => {
    try {
      const e = validateCouponForm(form, mode);
      setErrors(e);
      if (Object.keys(e).length > 0) return;

      setLoading(true);
      setMessage({ type: "", text: "" });

      const payload = {
        ...form,
        startDt: form.startDt
          ? new Date(form.startDt).toISOString().split("T")[0]
          : "",
        expiryDt: form.expiryDt
          ? new Date(form.expiryDt).toISOString().split("T")[0]
          : "",
      };

      let resp = null;

      if (mode === "create") {
        resp = await createNewCoupon(payload);
      } else {
        resp = await updateCoupon(payload, form.code);
      }

      if (resp?.data?.code === 200 || resp?.code === 200) {
        setMessage({
          type: "success",
          text:
            mode === "create"
              ? "Coupon created successfully!"
              : "Coupon updated successfully!",
        });

        setTimeout(() => {
          if (onSuccess) onSuccess();
          setMessage({ type: "", text: "" });
        }, 1500);

        return;
      }

      let err = resp?.data?.error || resp?.data?.message;

      if (Array.isArray(err)) {
        err = err.join("\n");
      }

      setMessage({
        type: "error",
        text: err || "Something went wrong",
      });
      if (
        resp?.data?.code === 400 &&
        resp?.data?.message === "startDt must be before expiryDt."
      ) {
        setMessage({
          type: "error",
          text: "Invalid expiry date: it must be greater than the start date.",
        });
        return;
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.couponFormContainer}>
        <h3 style={{ color: "white", marginBottom: 20 }}>
          {mode === "create" ? "Create Coupon" : "Update Coupon"}
        </h3>

        <Row className="my-3">
          <Col md={6}>
            <Input
              label="Code"
              disabled={mode === "update"}
              value={form.code}
              onChange={(e) =>
                handleChange("code", e.target.value.toUpperCase())
              }
              error={!!errors.code}
              helperText={errors.code}
              required
              placeholder="Enter Code"
            />
          </Col>

          <Col md={6}>
            <Input
              label="Title"
              value={form.title}
              onChange={(e) => handleChange("title", e.target.value)}
              error={!!errors.title}
              helperText={errors.title}
              placeholder="Enter Title"
              disabled={mode === "update"}
            />
          </Col>
        </Row>

        <Row className="my-3">
          <Col md={6}>
            <DropDown
              label="Discount Type"
              value={form.discountType}
              data={discountTypes}
              onChange={(v) => handleChange("discountType", v)}
              error={!!errors.discountType}
              helperText={errors.discountType}
              placeholder="Select Discount Type"
              disabled={mode === "update"}
            />
          </Col>

          <Col md={6}>
            <Input
              label="Discount Value"
              type="number"
              value={form.discountValue}
              onChange={(e) => handleChange("discountValue", e.target.value)}
              error={!!errors.discountValue}
              helperText={errors.discountValue}
              placeholder="Enter Discount Value"
              disabled={mode === "update"}
            />
          </Col>
        </Row>

        <Row className="my-3">
          <Col md={6} className={styles.fieldstyle}>
            <label className={styles.label}>
              Start Date <span className={styles.required}>*</span>
            </label>
            <DatePicker
              selected={form.startDt}
              onChange={(d) => handleChange("startDt", d)}
              placeholderText="Select Start Date"
              dateFormat="yyyy-MM-dd"
              minDate={new Date()}
              disabled={mode === "update"}
            />
            {errors.startDt && (
              <p className={styles.errorMessage}>{errors.startDt}</p>
            )}
          </Col>
          <Col md={6} className={styles.fieldstyle}>
            <label className={styles.label}>
              Expiry Date <span className={styles.required}>*</span>
            </label>
            <DatePicker
              selected={form.expiryDt}
              onChange={(d) => handleChange("expiryDt", d)}
              placeholderText="Select Expiry Date"
              dateFormat="yyyy-MM-dd"
              minDate={new Date()}
            />
            {errors.expiryDt && (
              <p className={styles.errorMessage}>{errors.expiryDt}</p>
            )}
          </Col>
        </Row>

        <Row className="my-3">
          <Col md={6}>
            <Input
              label="Usage Limit"
              type="number"
              value={form.usageLimit}
              onChange={(e) => handleChange("usageLimit", e.target.value)}
              placeholder="Enter Usage Limit"
              helperText={errors.usageLimit}
              error={!!errors.usageLimit}
            />
          </Col>
{/* 
          <Col md={6}>
            <Input
              label="Usage Count"
              type="number"
              disabled={mode === "update"}
              value={form.usageCount}
              onChange={(e) => handleChange("usageCount", e.target.value)}
              placeholder="Enter Usage Count"
              helperText={errors.usageCount}
              error={errors.usageCount}
            />
          </Col> */}
          <Col md={6}>
            <DropDown
              label="Status"
              data={couponStatusList}
              value={form.status}
              onChange={(v) => handleChange("status", v)}
              required
              placeholder="Select Status"
              helperText={errors.status}
              error={!!errors.status}
            />
          </Col>
        </Row>

        <Row className="my-3">
          <Col md={6}>
            <DropDown
              label="Partner"
              data={partnerList}
              value={form.partnerCode}
              onChange={(v) => handleChange("partnerCode", v)}
              error={!!errors.partnerCode}
              helperText={errors.partnerCode}
              required
              placeholder="Select Partner"
              disabled={mode === "update"}
              labelKey="partnerName"
              valueKey="partnerCd"
            />
          </Col>

          <Col md={6}>
            <DropDown
              label="Plan"
              data={planList}
              value={form.planId}
              onChange={(v) => handleChange("planId", v)}
              error={!!errors.planId}
              helperText={errors.planId}
              required
              placeholder="Select Plan"
              disabled={mode === "update"}
              labelKey="planCode"
              valueKey="planId"
            />
          </Col>
        </Row>

        <Row className="my-3">
          {/* <Col md={6}>
            <DropDown
              label="Status"
              data={couponStatusList}
              value={form.status}
              onChange={(v) => handleChange("status", v)}
              required
              placeholder="Select Status"
              helperText={errors.status}
              error={!!errors.status}
            />
          </Col> */}

          {mode === "update" && (
            <Col md={12}>
              <Input
                label="Comment"
                isTextArea
                value={form.comment}
                onChange={(e) => handleChange("comment", e.target.value)}
                required
                placeholder="Enter Comment"
                helperText={errors.comment}
                error={!!errors.comment}
                inputClassName={styles.commentBox}
              />
            </Col>
          )}
        </Row>

        {message.text && (
          <p
            style={{
              color: message.type === "success" ? "green" : "red",
              textAlign: "center",
              fontWeight: 500,
              whiteSpace: "pre-line",
            }}
          >
            {message.text}
          </p>
        )}

        <Row className="my-3">
          <Col className={styles.buttonContainer}>
            <div className={styles.centeredButtons}>
              <Button
                title={mode === "create" ? "Create Coupon" : "Update Coupon"}
                onClick={handleSubmit}
                loading={loading}
              />
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default CreateCoupon;
